import React from 'react';
import { Autocomplete, CircularProgress, TextField, createFilterOptions } from '@mui/material';

export interface SurgicalProcedureOption {
  value: string;
  label: string;
}

export interface SurgicalProcedureSelectorViewProps {
  /** Current stored value (the procedure name). */
  value: string;
  /** Catalog options to choose from. */
  options: SurgicalProcedureOption[];
  /** Whether the catalog is still loading. */
  loading?: boolean;
  /** Field label. */
  label?: React.ReactNode;
  /** Validation error state. */
  error?: boolean;
  /** Called with the selected procedure name (or "" when cleared). */
  onChange: (value: string) => void;
  disabled?: boolean;
}

// SHER-459: cap the visible matches so a 100+ procedure catalog never runs the
// dropdown off-screen — same affordance as the billing doctor selector.
const filterOptions = createFilterOptions<SurgicalProcedureOption>({
  limit: 15,
  stringify: (option) => option.label,
});

/**
 * Presentational searchable selector for the surgical procedures catalog,
 * used by the operative report "Geste principal" field (SHER-459).
 *
 * Pure on purpose: it takes its options as a prop so it can be exercised in
 * Storybook and unit tests without the investigation/services data layer. The
 * container (`SurgicalProcedureSelector`) wires it to the live catalog.
 */
const SurgicalProcedureSelectorView: React.FC<SurgicalProcedureSelectorViewProps> = ({
  value,
  options,
  loading = false,
  label,
  error = false,
  onChange,
  disabled = false,
}) => {
  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));
  const selectedOption = sortedOptions.find((option) => option.value === value) || null;

  return (
    <Autocomplete
      options={sortedOptions}
      value={selectedOption}
      loading={loading}
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      onChange={(_event, newValue) => onChange(newValue ? newValue.value : '')}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          margin="dense"
          error={error}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      fullWidth
    />
  );
};

export default SurgicalProcedureSelectorView;
